import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import type { PlanPoint } from "@sts/geometry";
import { round } from "./plan-marks.jsx";

/**
 * A comment's pin on the drawing: a small map pin whose tip is on the spot
 * the comment is about, with the thread's number in its head — the number the
 * Comments panel, the installer pack and the plan exports use for it.
 *
 *   open      amber, white number
 *   resolved  hollow grey (drawn only while the panel's Resolved list is open)
 *   draft     dashed, "+": dropped, not yet posted
 *
 * Coloured with attributes rather than a stylesheet rule, so the pin is right
 * whatever stylesheet is around it (the lesson of 5cd31e5).
 */

const OPEN = "#c77700";
const RESOLVED = "#8a8a8a";
const SELECTED = "#0f766e";

/** Head radius and how far above the tip its centre sits, in screen px. */
const HEAD_R = 10;
const HEAD_UP = 17;

export type CommentPinLook = "open" | "resolved" | "draft";

export function CommentPinMark({
  id,
  at,
  number,
  look,
  selected = false,
  detached = false,
  interactive = false,
  label,
  onPick,
}: {
  readonly id: string;
  /** The tip, in view units. */
  readonly at: PlanPoint;
  readonly number: number | null;
  readonly look: CommentPinLook;
  readonly selected?: boolean;
  /** Its element has gone from the plan: drawn where it was dropped, outlined dashed. */
  readonly detached?: boolean;
  readonly interactive?: boolean;
  /** What a screen reader hears for it. */
  readonly label?: string;
  readonly onPick?: (id: string) => void;
}): ReactNode {
  const x = round(at.x);
  const y = round(at.y);
  const cy = round(at.y - HEAD_UP);
  const fill = look === "open" ? OPEN : "#fff";
  const ink = look === "open" ? "#fff" : look === "draft" ? OPEN : RESOLVED;
  const edge = selected ? SELECTED : look === "open" ? "#fff" : look === "draft" ? OPEN : RESOLVED;
  // The tail: from the tip up to where it meets the head.
  const tail = `M ${x},${y} L ${round(at.x - 5.5)},${round(at.y - HEAD_UP + 8)} L ${round(at.x + 5.5)},${round(at.y - HEAD_UP + 8)} Z`;
  const text = number === null ? "+" : String(number);
  return (
    <g
      className={`floor-plan__comment-pin floor-plan__comment-pin--${look}${selected ? " is-selected" : ""}`}
      data-comment-id={id}
      data-comment-number={number ?? undefined}
      data-look={look}
      {...(detached ? { "data-detached": "true" } : {})}
      {...(interactive
        ? {
            role: "button",
            tabIndex: 0,
            "aria-label": label ?? `Comment ${text}`,
            "aria-pressed": selected,
            onPointerDown: (event: ReactPointerEvent<SVGGElement>) => {
              // The press belongs to the pin: never the start of a pan.
              if (event.button === 0) event.stopPropagation();
            },
            onClick: (event: ReactMouseEvent<SVGGElement>) => {
              event.stopPropagation();
              onPick?.(id);
            },
            onKeyDown: (event: ReactKeyboardEvent<SVGGElement>) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onPick?.(id);
              }
            },
          }
        : { "aria-hidden": true })}
      style={interactive ? { cursor: "pointer" } : { pointerEvents: "none" }}
      opacity={look === "resolved" && !selected ? 0.85 : 1}
    >
      {selected ? <circle cx={x} cy={cy} r={HEAD_R + 4} fill="none" stroke={SELECTED} strokeWidth={2} /> : null}
      <path d={tail} fill={look === "open" ? OPEN : edge} stroke="none" />
      <circle
        cx={x}
        cy={cy}
        r={HEAD_R}
        fill={fill}
        stroke={edge}
        strokeWidth={look === "open" && !selected ? 1.5 : 2}
        strokeDasharray={look === "draft" || detached ? "3 2" : undefined}
      />
      <circle cx={x} cy={y} r={1.8} fill={look === "open" ? OPEN : edge} />
      <text
        x={x}
        y={round(at.y - HEAD_UP + 4)}
        textAnchor="middle"
        fontSize={text.length > 2 ? 9 : 11}
        fontWeight={700}
        fill={ink}
        style={{ userSelect: "none" }}
      >
        {text}
      </text>
    </g>
  );
}
